<template>
  <div class="container mt-4">
    <div class="row mb-4">
      <div class="col">
        <h2>Export Database Schema</h2>
        <p class="text-muted">Export database schema and stored procedures to a ZIP file</p>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">Select Database</h5>

            <div class="mb-3">
              <label for="databaseSelect" class="form-label">Database</label>
              <Multiselect
                v-model="selectedDatabase"
                class="form-control"
                placeholder="Choose database"
                :open-direction="'bottom'"
                :close-on-select="true"
                :groups="true"
                :options="groupedDatabases"
                mode="single"
                :disabled="loading.databases || loading.export"
                :style="{ '--ms-max-height': '30rem' }"
              />
              <div class="form-text">Choose the database you want to export</div>
            </div>

            <button
              @click="handleExport"
              class="btn btn-primary"
              :disabled="!selectedDatabase || loading.export"
            >
              <span
                v-if="loading.export"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="fas fa-download me-2"></i>
              Export to ZIP
            </button>

            <div v-if="exportStatus === 'success'" class="alert alert-success mt-3">
              <i class="fas fa-check-circle me-2"></i>
              Export completed successfully!
            </div>

            <div v-if="exportStatus === 'error'" class="alert alert-danger mt-3">
              <i class="fas fa-exclamation-circle me-2"></i>
              Export failed. Please try again.
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">
              <i class="fas fa-info-circle me-2"></i>Export Contents
            </h5>
            <p class="mb-3">The exported ZIP file will include organized SQL scripts for:</p>

            <div class="row">
              <div class="col-6">
                <ul class="list-unstyled">
                  <li><i class="fas fa-table text-primary me-2"></i>Tables</li>
                  <li><i class="fas fa-cogs text-primary me-2"></i>Stored Procedures</li>
                  <li><i class="fas fa-code text-primary me-2"></i>Functions</li>
                  <li><i class="fas fa-eye text-primary me-2"></i>Views</li>
                </ul>
              </div>
              <div class="col-6">
                <ul class="list-unstyled">
                  <li><i class="fas fa-link text-primary me-2"></i>Foreign Keys</li>
                  <li><i class="fas fa-list-ol text-primary me-2"></i>Indexes</li>
                  <li><i class="fas fa-users text-primary me-2"></i>Users & Roles</li>
                  <li><i class="fas fa-folder text-primary me-2"></i>Schemas</li>
                </ul>
              </div>
            </div>

            <hr />

            <h6 class="mb-2">File Structure</h6>
            <p class="small text-muted mb-0">
              Scripts are organized into folders by object type, making it easy to review and
              version control your database schema.
            </p>
          </div>
        </div>

        <div class="card mt-3">
          <div class="card-body">
            <h5 class="card-title">
              <i class="fas fa-lightbulb me-2"></i>Usage Tips
            </h5>
            <ul class="small mb-0">
              <li>Use exports to version control your database schema</li>
              <li>Compare exports to track schema changes over time</li>
              <li>Share schema with team members or environments</li>
              <li>Create backups of your database structure</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { compareStore } from '@/stores/compare'
import { authStore } from '@/stores/auth'
import Multiselect from '@vueform/multiselect'

const store = compareStore()
const auth = authStore()

const selectedDatabase = ref('')
const databases = ref([])
const loading = ref({
  databases: false,
  export: false,
})
const exportStatus = ref(null)

const groupedDatabases = computed(() => {
  if (!databases.value || databases.value.length === 0) return []

  // Group data by the 'group' field
  const groupedData = databases.value.reduce((acc, item) => {
    const label = item.group

    // If the label doesn't exist in the accumulator, create it
    if (!acc[label]) {
      acc[label] = []
    }

    // Add the database name to the appropriate group
    acc[label].push(item.databaseName)

    return acc
  }, {})

  // Convert grouped data into the desired format
  return Object.keys(groupedData).map((label) => ({
    label,
    options: groupedData[label].sort(),
  }))
})

onMounted(async () => {
  loading.value.databases = true
  try {
    databases.value = await store.getDatabases()
  } catch (error) {
    console.error('Failed to load databases:', error)
  } finally {
    loading.value.databases = false
  }
})

async function handleExport() {
  if (!selectedDatabase.value) return

  exportStatus.value = null
  loading.value.export = true

  try {
    const apiUrl = window.config ? window.config.apiUrl : ''
    const response = await fetch(
      `${apiUrl}/database/export?database=${encodeURIComponent(selectedDatabase.value)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error('Export failed')
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `${selectedDatabase.value}_export.zip`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Download file
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)

    exportStatus.value = 'success'

    // Clear success message after 3 seconds
    setTimeout(() => {
      exportStatus.value = null
    }, 3000)
  } catch (error) {
    console.error('Export error:', error)
    exportStatus.value = 'error'
  } finally {
    loading.value.export = false
  }
}
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.list-unstyled li {
  padding: 0.25rem 0;
}

.btn-primary {
  font-weight: 500;
}
</style>
